// src/components/doctor/ContextDrawer.jsx
//
// Tier 3 — reference on demand. A right-hand drawer, closed by default, with
// four tabs (Attention, Report, History, Documents). Narrower than 900px it is a
// modal bottom sheet: it traps focus (useDialogFocus) and returns it to the
// opener on close. On desktop it is a complementary region beside the work that
// does not trap focus. Esc closes either.

import React, { useEffect, useRef } from 'react';
import useDialogFocus from '../../hooks/useDialogFocus';
import { AttentionTab, ReportTab, HistoryTab, DocumentsTab } from './ContextTabs';

export const DRAWER_TABS = [
  { key: 'attention', label: 'Attention' },
  { key: 'report', label: 'Report' },
  { key: 'history', label: 'History' },
  { key: 'documents', label: 'Documents' },
];

function DrawerBody({ tab, onTab, onClose, attentionCount, c, modal }) {
  const tabRefs = useRef({});
  const onTabKey = e => {
    const i = DRAWER_TABS.findIndex(t => t.key === tab);
    let n = null;
    if (e.key === 'ArrowRight') n = (i + 1) % DRAWER_TABS.length;
    else if (e.key === 'ArrowLeft') n = (i - 1 + DRAWER_TABS.length) % DRAWER_TABS.length;
    else if (e.key === 'Home') n = 0;
    else if (e.key === 'End') n = DRAWER_TABS.length - 1;
    if (n == null) return;
    e.preventDefault();
    onTab(DRAWER_TABS[n].key);
    tabRefs.current[DRAWER_TABS[n].key]?.focus();
  };
  return (
    <>
      <div className="consult-drawer__head">
        <h2 className="consult-drawer__title" id="consult-drawer-title" tabIndex={-1}>Patient context</h2>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close context">✕</button>
      </div>
      <div className="consult-dtabs" role="tablist" aria-label="Context sections" onKeyDown={onTabKey}>
        {DRAWER_TABS.map(t => (
          <button key={t.key} type="button" role="tab" id={`dtab-${t.key}`} className="consult-dtab" aria-selected={tab === t.key}
            aria-controls="consult-dpanel" tabIndex={tab === t.key ? 0 : -1} ref={el => { tabRefs.current[t.key] = el; }} onClick={() => onTab(t.key)}>
            {t.label}
            {t.key === 'attention' && attentionCount > 0 && <span className="consult-dtab__n">{attentionCount}<span className="sr-only"> urgent</span></span>}
          </button>
        ))}
      </div>
      <div className="consult-drawer__body" id="consult-dpanel" role="tabpanel" aria-labelledby={`dtab-${tab}`}>
        {tab === 'attention' && <AttentionTab context={c.context} onToggle={c.toggleItem} />}
        {tab === 'report' && <ReportTab context={c.context} onOpenRecord={c.onOpenRecord} />}
        {tab === 'history' && (
          <HistoryTab history={c.history} loadHistory={c.loadHistory} previousRx={c.previousRx} rxLoading={c.allRx === null}
            locked={c.locked} onCopy={c.copyMedicines} />
        )}
        {tab === 'documents' && <DocumentsTab patientId={c.patientId} onOpenRecord={c.onOpenRecord} />}
      </div>
    </>
  );
}

// The modal wrapper exists as its own component so useDialogFocus mounts and
// unmounts with the sheet (it captures the opener on mount and restores it on unmount).
function ModalSheet({ onClose, children }) {
  const ref = useRef(null);
  useDialogFocus(ref);
  useEffect(() => { ref.current?.querySelector('[role="tab"][aria-selected="true"]')?.focus(); }, []);
  return (
    <>
      <div className="consult-sheet-backdrop" onMouseDown={onClose} />
      <aside className="card consult-drawer" role="dialog" aria-modal="true" aria-labelledby="consult-drawer-title" ref={ref} data-testid="context-drawer">
        {children}
      </aside>
    </>
  );
}

export default function ContextDrawer({ open, modal, tab, onTab, onClose, attentionCount, ctx }) {
  const asideRef = useRef(null);
  const opener = useRef(null);

  // Esc closes (unless a dialog is open on top of it).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => { if (e.key === 'Escape' && !document.querySelector('.dialog-backdrop')) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Desktop (non-modal): move focus in when opened, hand it back when closed.
  useEffect(() => {
    if (!open || modal) return undefined;
    opener.current = document.activeElement;
    asideRef.current?.querySelector('[role="tab"][aria-selected="true"]')?.focus();
    return () => {
      const el = opener.current;
      if (el && typeof el.focus === 'function' && document.contains(el)) el.focus();
    };
  }, [open, modal]);

  if (!open) return null;
  const body = <DrawerBody tab={tab} onTab={onTab} onClose={onClose} attentionCount={attentionCount} c={ctx} modal={modal} />;
  if (modal) return <ModalSheet onClose={onClose}>{body}</ModalSheet>;
  return (
    <aside className="card consult-drawer" aria-labelledby="consult-drawer-title" ref={asideRef} data-testid="context-drawer">
      {body}
    </aside>
  );
}
